"use client";

import React, { useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { motion } from "framer-motion";
import { IconBrandGithub, IconBrandGoogle, IconMail } from "@tabler/icons-react";
import { 
  createUserWithEmailAndPassword, 
  signInWithPopup, 
  GithubAuthProvider,
  GoogleAuthProvider,
  updateProfile 
} from "firebase/auth";
import { Header } from "@/components/sections/header";
import { Footer } from "@/components/sections/footer";

import { cn } from "@/lib/utils";
import { auth } from "@/lib/firebase";
import { useToast } from "@/components/ui/toast-provider";
import FloatingLabelInput from "@/components/ui/floating-label-input";
import AnimatedButton from "@/components/ui/animated-button";
import { Testimonial3DCardStack } from "@/components/ui/testimonial-3d-card";

export default function RegisterPage() {
  return (
    <main className="w-full max-w-[1920px] mx-auto">
      <Header />
      <div className="w-full min-h-screen grid grid-cols-1 lg:grid-cols-2">
        <RegisterForm />
        <div className="hidden lg:flex lg:flex-col lg:justify-center">
          <AmbientColor />
          <TestimonialSection />
        </div>
      </div>
      <Footer />
    </main>
  );
}

function RegisterForm() {
  const router = useRouter();
  const { addToast } = useToast();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
  });

  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      // Make sure auth is initialized
      if (!auth) {
        throw new Error("Authentication service is not available");
      }
      
      // Validate password strength
      if (formData.password.length < 6) {
        throw new Error("Password must be at least 6 characters long");
      }
      
      // Create user with Firebase
      const userCredential = await createUserWithEmailAndPassword(
        auth, 
        formData.email, 
        formData.password
      );
      
      // Update profile with name
      await updateProfile(userCredential.user, {
        displayName: formData.name
      });
      
      // Show success toast
      addToast({
        title: "Registration successful",
        description: "Welcome to SpectrumWebCo!",
        type: "success",
        duration: 3000
      });
      
      // Redirect to API Keys page after successful registration
      router.push("/account/api-keys");
    } catch (error: any) {
      console.error("Registration error:", error);
      
      // Show error toast with appropriate message
      let errorMessage = "An error occurred during registration";
      if (error.code === 'auth/email-already-in-use') {
        errorMessage = "This email is already in use";
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = "Invalid email address";
      } else if (error.code === 'auth/weak-password') {
        errorMessage = "Password is too weak";
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      addToast({
        title: "Registration failed",
        description: errorMessage,
        type: "error",
        duration: 5000
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleGitHubSignUp = async () => {
    try {
      setIsLoading(true);
      
      // Make sure auth is initialized
      if (!auth) {
        throw new Error("Authentication service is not available");
      }
      
      // Sign up with GitHub
      const provider = new GithubAuthProvider();
      await signInWithPopup(auth, provider);
      
      // Show success toast
      addToast({
        title: "Registration successful",
        description: "Welcome to SpectrumWebCo!",
        type: "success",
        duration: 3000
      });
      
      // Redirect to API Keys page after successful registration
      router.push("/account/api-keys");
    } catch (error: any) {
      console.error("GitHub registration error:", error);
      
      let errorMessage = "An error occurred with GitHub sign up";
      if (error.code === 'auth/popup-closed-by-user') {
        errorMessage = "Sign up was cancelled";
      } else if (error.code === 'auth/account-exists-with-different-credential') {
        errorMessage = "An account already exists with the same email address but different sign-in credentials";
      }
      
      addToast({
        title: "Registration failed",
        description: errorMessage,
        type: "error",
        duration: 5000
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignUp = async () => {
    try {
      setIsLoading(true);
      
      // Make sure auth is initialized
      if (!auth) {
        throw new Error("Authentication service is not available");
      }
      
      // Sign up with Google
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
      
      // Show success toast
      addToast({
        title: "Registration successful",
        description: "Welcome to SpectrumWebCo!",
        type: "success",
        duration: 3000
      });
      
      // Redirect to API Keys page after successful registration
      router.push("/account/api-keys");
    } catch (error: any) {
      console.error("Google registration error:", error);
      
      let errorMessage = "An error occurred with Google sign up";
      if (error.code === 'auth/popup-closed-by-user') {
        errorMessage = "Sign up was cancelled";
      } else if (error.code === 'auth/account-exists-with-different-credential') {
        errorMessage = "An account already exists with the same email address but different sign-in credentials";
      }
      
      addToast({
        title: "Registration failed",
        description: errorMessage,
        type: "error",
        duration: 5000
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full h-full flex items-center justify-center p-4 sm:p-8">
      <form 
        className="w-full max-w-md flex flex-col items-center justify-center"
        onSubmit={handleSubmit}
      >
        <Logo />
        <h1 className="my-4 text-xl font-bold text-neutral-800 dark:text-neutral-100 md:text-4xl">
          Sign up for an account
        </h1>

        <div className="w-full space-y-6 mt-8">
          <FloatingLabelInput
            id="name"
            label="Full name"
            type="text"
            required
            value={formData.name}
            onChange={handleChange}
          />

          <FloatingLabelInput
            id="email"
            label="Email address"
            type="email"
            required
            value={formData.email}
            onChange={handleChange}
            icon={<IconMail size={18} />}
          />

          <FloatingLabelInput
            id="password"
            label="Password"
            type="password"
            required
            value={formData.password}
            onChange={handleChange}
          />

          <div className="pt-2">
            <AnimatedButton
              type="submit"
              fullWidth
              isLoading={isLoading}
            >
              Sign Up
            </AnimatedButton>
            
            <p className={cn("text-sm text-neutral-600 text-center mt-4 dark:text-neutral-400")}>
              Already have an account?{" "}
              <Link href="/auth/login" className="text-emerald-600 hover:text-emerald-500 dark:text-emerald-400 dark:hover:text-emerald-300 font-medium">
                Sign in
              </Link>
            </p>
          </div>
        </div>

        <div className="my-8 w-full">
          <div className="relative">
            <div
              className="absolute inset-0 flex items-center"
              aria-hidden="true"
            >
              <div className="w-full border-t border-neutral-300 dark:border-neutral-700" />
            </div>
            <div className="relative flex justify-center text-sm font-medium leading-6">
              <span className="bg-white px-6 text-neutral-400 dark:text-neutral-500 dark:bg-neutral-950">
                Or continue with
              </span>
            </div>
          </div>

          <div className="mt-6 w-full grid grid-cols-2 gap-4">
            <AnimatedButton
              variant="secondary"
              leftIcon={<IconBrandGithub className="h-5 w-5" />}
              onClick={handleGitHubSignUp}
              isLoading={isLoading}
            >
              <span className="text-sm font-semibold leading-6">
                GitHub
              </span>
            </AnimatedButton>
            
            <AnimatedButton
              variant="secondary"
              leftIcon={<IconBrandGoogle className="h-5 w-5" />}
              onClick={handleGoogleSignUp}
              isLoading={isLoading}
            >
              <span className="text-sm font-semibold leading-6">
                Google
              </span>
            </AnimatedButton>
          </div>

          <p className="text-neutral-600 dark:text-neutral-400 text-sm text-center mt-8">
            By signing up, you agree to our{" "}
            <Link
              href="/terms"
              className="text-emerald-600 hover:text-emerald-500 dark:text-emerald-400 dark:hover:text-emerald-300"
            >
              Terms of Service
            </Link>{" "}
            and{" "}
            <Link
              href="/privacy-policy"
              className="text-emerald-600 hover:text-emerald-500 dark:text-emerald-400 dark:hover:text-emerald-300"
            >
              Privacy Policy
            </Link>
          </p>
        </div>
      </form>
    </div>
  );
}

function TestimonialSection() {
  const testimonials = [
    {
      id: "1",
      name: "John Doe",
      role: "Senior Engineer at Acme Inc",
      content: "This platform has completely transformed our workflow. The speed at which we can deploy and the reliability of the system is unlike anything I've experienced before.",
      avatarSrc: "https://images.unsplash.com/photo-1599566150163-29194dcaad36?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=3387&q=80",
    },
    {
      id: "2",
      name: "Robert Johnson",
      role: "CTO at TechFlow",
      content: "The development tools provided have cut our time-to-market by 40%. Amazing platform with outstanding support. Would highly recommend to any serious development team.",
      avatarSrc: "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mnx8YXZhdGFyfGVufDB8fDB8fHww&auto=format&fit=crop&w=800&q=60",
    },
    {
      id: "3",
      name: "Jane Smith",
      role: "Lead Developer at InnovateTech",
      content: "I've used many developer platforms, but this one stands out with its intuitive interface and powerful features. It's become an essential part of our tech stack.",
      avatarSrc: "https://images.unsplash.com/photo-1580489944761-15a19d654956?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8NXx8YXZhdGFyfGVufDB8fDB8fHww&auto=format&fit=crop&w=800&q=60",
    },
  ];

  return (
    <div className="w-full h-full flex items-center justify-center p-8">
      <div className="w-full h-[500px] max-w-lg mx-auto relative">
        <Testimonial3DCardStack testimonials={testimonials} />
        <div className="mt-12 text-center">
          <p className={cn("font-semibold text-xl text-neutral-600 dark:text-neutral-400")}>
            People love us
          </p>
          <p className={cn("font-normal text-base text-neutral-500 dark:text-neutral-400 mt-8")}>
            Our platform is loved by thousands of engineers across the world, be part
            of the community and join us.
          </p>
        </div>
      </div>
    </div>
  );
}

const Logo = () => {
  return (
    <Link
      href="/"
      className="font-normal flex space-x-2 items-center text-sm mr-4 text-black px-2 py-1 relative z-20 dark:text-white"
    >
      <div className="w-8 h-8 relative overflow-hidden rounded-md">
        <Image
          src="/spectrumwebco-transparent.png"
          alt="SpectrumWebCo"
          width={30}
          height={30}
          className="object-contain"
        />
      </div>
      <span className="font-medium text-black dark:text-white">SpectrumWebCo</span>
    </Link>
  );
};

export const AmbientColor = () => {
  return (
    <div className="pointer-events-none absolute left-0 top-0 z-40 h-screen w-screen">
      <div
        style={{
          transform: "translateY(-350px) rotate(-45deg)",
          width: "560px",
          height: "1380px",
          background:
            "radial-gradient(68.54% 68.72% at 55.02% 31.46%, hsla(160, 100%, 85%, .08) 0, hsla(160, 100%, 55%, .02) 50%, hsla(160, 100%, 45%, 0) 80%)",
        }}
        className="absolute left-0 top-0"
      />

      <div
        style={{
          transform: "rotate(-45deg) translate(5%, -50%)",
          transformOrigin: "top left",
          width: "240px",
          height: "1380px",
          background:
            "radial-gradient(50% 50% at 50% 50%, hsla(160, 100%, 85%, .06) 0, hsla(160, 100%, 55%, .02) 80%, transparent 100%)",
        }}
        className="absolute left-0 top-0"
      />

      <div
        style={{
          position: "absolute",
          borderRadius: "20px",
          transform: "rotate(-45deg) translate(-180%, -70%)",
          transformOrigin: "top left",
          top: 0,
          left: 0,
          width: "240px",
          height: "1380px",
          background:
            "radial-gradient(50% 50% at 50% 50%, hsla(160, 100%, 85%, .04) 0, hsla(160, 100%, 45%, .02) 80%, transparent 100%)",
        }}
        className="absolute left-0 top-0"
      />
    </div>
  );
};
